import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { enqueueSnackbar } from 'notistack'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { useBookingContext } from '@providers/booking/booking_context'
import { STICKY_FOOTER_HEIGHT_PX } from '@shared/constants'
import { i18WithParams as t, i18WithComponent as tc } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { OfferTypeAvatar, ServiceTypeAvatar } from 'components-ui/src/icon_avatar/icon_avatar'
import { buildErrorMessage } from 'components-ui/src/shared/error'
import { CustomError } from 'components-ui/src/shared/error_handler'
import { buildReservationPayload } from 'hooks/use_booking_reservation'
import { ClientWaitListService } from 'service-api/src/services/client_waitlist.service'
import { captureException } from 'service-api/src/shared'
import { CreateWaitlistOrderClientInput } from 'service-api/src/types/api'
import { buildWaitListOffers } from './helper'
import type { TWaitListOffer, TWaitListOfferAddOn } from './types'
import { WaitlistConfirmation } from './waitlist_confirmation'
import { WaitlistContactForm } from './waitlist_contact_form'

const OfferDescription = ({ offer }: { offer: TWaitListOffer }) => {
  return (
    <Stack minWidth={0}>
      {offer.period && (
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {offer.period}
        </Typography>
      )}
      <Stack direction="row" columnGap={1} alignItems="baseline" flexWrap="wrap">
        <Typography variant="h6">{offer.offerName}:</Typography>
        <Typography variant="body2" sx={{ lineHeight: '1' }}>
          {offer.petName}
        </Typography>
      </Stack>
    </Stack>
  )
}

const AddOnDescription = ({ addOn }: { addOn: TWaitListOfferAddOn }) => {
  return (
    <Stack direction="row" columnGap={0.75} alignItems="baseline" flexWrap="wrap" minWidth={0}>
      <Typography variant="h6">{addOn.offerName}:</Typography>
      <Typography variant="body2" sx={{ lineHeight: '1' }}>
        {addOn.petName}
      </Typography>
    </Stack>
  )
}

const AddOnAvatar = ({ offer, addOn }: { offer: TWaitListOffer; addOn: TWaitListOfferAddOn }) => {
  if (addOn.offerType === 'grooming') {
    return <ServiceTypeAvatar size="small" aspect="square" serviceType={offer.serviceType} />
  }
  return <OfferTypeAvatar size="small" fontSize="small" aspect="square" offerType={addOn.offerType} />
}

const Underline = () => {
  return (
    <Typography component="span" variant="body2bold" sx={{ textDecoration: 'underline' }}>
      {t('global.label.not')}
    </Typography>
  )
}

export const Waitlist = () => {
  const bookingCtx = useBookingContext()
  const { state: session } = useSessionContext()
  const { location } = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const { navigateRoute } = useRoutes()
  const offers = useMemo(() => buildWaitListOffers(bookingCtx.state), [bookingCtx.state])
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'waitlistConfirm') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'waitlistConfirm' } })
    }
  }, [bookingCtx])

  useEffect(() => {
    if (!bookingCtx.state.startDate) {
      navigateRoute('searchServiceType', { serviceTypeName: 'boarding' })
    }
  }, [bookingCtx.state.startDate, navigateRoute])

  const onBackHandler = useCallback(() => {
    bookingCtx.dispatch({ type: 'PARTIAL_RESET_BOOKING_STATE' })
    navigateRoute('searchOffersResults', { serviceTypeName })
  }, [bookingCtx, navigateRoute, serviceTypeName])

  const onConfirmWaitlist = useCallback(async (): Promise<boolean> => {
    const payload = buildReservationPayload(bookingCtx.state)
    if (!payload) return false

    // move requestPeriods to main object
    payload.reservations.forEach((res) => {
      if (res.requestPeriods && res.requestPeriods.length > 0) {
        res.startDate = res.requestPeriods[0].startDate
        res.endDate = res.requestPeriods[0].endDate
        delete res.requestPeriods
      }
    })

    try {
      await ClientWaitListService.create(locationName, payload as CreateWaitlistOrderClientInput)
      setShowConfirmation(true)
    } catch (error) {
      const msg = buildErrorMessage(error)
      enqueueSnackbar(msg, { variant: 'error' })
      captureException(new CustomError(msg, 'Waitlist.create', { cause: error }))
      return false
    }
    return true
  }, [bookingCtx.state, locationName])

  return showConfirmation ? (
    <WaitlistConfirmation />
  ) : (
    <Box
      sx={{
        maxWidth: 700,
        margin: '0 auto',
        px: { xs: 2, sm: 3 },
        paddingTop: 3,
        paddingBottom: `${STICKY_FOOTER_HEIGHT_PX + 32}px`,
      }}
    >
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1 }}>
        <Typography variant="body2bold" color="error" sx={{ textTransform: 'capitalize' }}>
          {tc('waitlist.lead', { not: <Underline /> })}
        </Typography>

        <Typography variant="h2">{t('waitlist.title')}</Typography>

        <Stack spacing={1.5} mt={2}>
          {offers.map((offer) => (
            <Fragment key={offer.id}>
              <Stack
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
              >
                <ServiceTypeAvatar size="large" aspect="square" serviceType={offer.serviceType} />
                <OfferDescription offer={offer} />
              </Stack>

              {offer.addOns.map((addOn) => (
                <Stack key={addOn.id} direction="row" gap={1} alignItems="center" sx={{ pl: { xs: 1, sm: 2 } }}>
                  <SubdirectoryArrowRightIcon sx={{ flexShrink: 0, color: theme.palette.action.disabled }} />
                  <Stack
                    direction="row"
                    gap={1.5}
                    alignItems="center"
                    sx={{ width: '100%', p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <AddOnAvatar offer={offer} addOn={addOn} />
                    <AddOnDescription addOn={addOn} />
                  </Stack>
                </Stack>
              ))}
            </Fragment>
          ))}
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mt: 3, borderRadius: 1 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {t('detailsPolicies.contactInformation')}
        </Typography>
        <WaitlistContactForm
          key={`${locationName}:${session.user?.lupId || ''}`}
          locationName={locationName}
          facilityName={location?.displayName || ''}
          lupId={session.user?.lupId || ''}
          onBack={onBackHandler}
          onConfirm={onConfirmWaitlist}
        />
      </Paper>
    </Box>
  )
}
